import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, Upload, CheckCircle, AlertCircle, X } from 'lucide-react';

const API_URL = 'https://api.ihirefast.com';

interface FormField {
  id: string;
  type: 'text' | 'email' | 'phone' | 'textarea' | 'select' | 'file' | 'checkbox';
  label: string;
  placeholder?: string;
  required: boolean;
  options?: string[];
  accept?: string;
  maxFileSize?: number;
  order: number;
}

interface FormData {
  id: string;
  name: string;
  description: string | null;
  job_title: string;
  status: string;
  fields: FormField[];
  settings: Record<string, string>;
}

interface Branding {
  company_name?: string;
  display_name?: string;
  logo_url?: string;
  form_background_color?: string;
  primary_color?: string;
  form_logo_url?: string;
  form_background_url?: string;
  form_company_name?: string;
}

export default function ApplyPage() {
  const { slug } = useParams<{ slug: string }>();
  const [form, setForm] = useState<FormData | null>(null);
  const [branding, setBranding] = useState<Branding | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [fieldFiles, setFieldFiles] = useState<Record<string, File>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    fetch(`${API_URL}/forms/public/${slug}`)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          if (res.status === 404) throw new Error('Form not found');
          if (res.status === 410) throw new Error(body.error || 'This application form is no longer accepting submissions');
          throw new Error(body.error || `Failed to load form (${res.status})`);
        }
        return res.json();
      })
      .then((data) => {
        setForm(data.form);
        // Merge workspace branding with per-form branding overrides
        const wb = data.branding || {};
        const fb = data.form_branding || {};
        setBranding({
          ...wb,
          // Form-level overrides
          form_company_name: fb.company_name || undefined,
          form_background_color: fb.background_color || wb.form_background_color,
          primary_color: fb.primary_color || wb.primary_color,
          form_logo_url: fb.form_logo_url || undefined,
          form_background_url: fb.form_background_url || undefined,
        });
        // Initialize field values
        const initial: Record<string, string> = {};
        (data.form.fields || []).forEach((f: FormField) => {
          initial[f.id] = f.type === 'checkbox' ? '' : '';
        });
        setFieldValues(initial);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [slug]);

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!form) return false;

    for (const field of form.fields) {
      if (field.required) {
        if (field.type === 'file') {
          if (!fieldFiles[field.id]) {
            errors[field.id] = 'This field is required';
          }
        } else if (!fieldValues[field.id]?.trim()) {
          errors[field.id] = 'This field is required';
        }
      }
      if (field.type === 'email' && fieldValues[field.id]) {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fieldValues[field.id])) {
          errors[field.id] = 'Please enter a valid email address';
        }
      }
      if (field.type === 'phone' && fieldValues[field.id]) {
        const digitsOnly = fieldValues[field.id].replace(/\D/g, '');
        if (digitsOnly.length < 10) {
          errors[field.id] = 'Please enter a valid phone number';
        }
      }
      if (field.type === 'file' && fieldFiles[field.id]) {
        const maxBytes = (field.maxFileSize || 10) * 1024 * 1024;
        if (fieldFiles[field.id].size > maxBytes) {
          errors[field.id] = `File must be under ${field.maxFileSize || 10}MB`;
        }
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !form || !slug) return;

    setSubmitting(true);
    try {
      const formData = new globalThis.FormData();

      // Add text field values
      for (const field of form.fields) {
        if (field.type !== 'file') {
          let value = fieldValues[field.id] || '';
          // Auto-format phone: strip non-digit chars before submission
          if (field.type === 'phone' && value) {
            value = value.replace(/\D/g, '');
          }
          formData.append(`field_${field.id}`, value);
        }
      }

      // Add files
      for (const field of form.fields) {
        if (field.type === 'file' && fieldFiles[field.id]) {
          formData.append(`file_${field.id}`, fieldFiles[field.id]);
        }
      }

      const res = await fetch(`${API_URL}/forms/public/${slug}/submit`, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Submission failed');
      }

      const result = await res.json();
      setSubmitted(true);

      if (result.redirect_url) {
        setTimeout(() => {
          window.location.href = result.redirect_url;
        }, 2000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error && !form) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Oops</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mb-3"
          >
            Try Again
          </button>
          <br />
          <Link to="/" className="text-blue-600 hover:underline text-sm">
            Go to iHireFast.com
          </Link>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Application Submitted!</h1>
          <p className="text-gray-600 mb-6">
            {form?.settings?.confirmation_message ||
              'Thank you for your application. We will be in touch soon.'}
          </p>
          <p className="text-sm text-gray-400">Powered by iHireFast</p>
        </div>
      </div>
    );
  }

  if (!form) return null;

  const bgColor = branding?.form_background_color || '#f8fafc';
  const btnColor = branding?.primary_color || '#2563eb';
  const companyLabel = branding?.form_company_name || branding?.display_name || branding?.company_name;
  const logoUrl = branding?.form_logo_url || branding?.logo_url;
  const bgImageUrl = branding?.form_background_url;

  return (
    <div
      className="min-h-screen py-8 px-4"
      style={{
        backgroundColor: bgColor || '#f9fafb',
        ...(bgImageUrl ? {
          backgroundImage: `url(${bgImageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        } : {}),
      }}
    >
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={companyLabel || 'Company logo'}
              className="w-12 h-12 rounded-xl object-contain mx-auto mb-4"
            />
          ) : (
            <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-600 rounded-xl mb-4">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          )}
          {companyLabel && (
            <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">{companyLabel}</p>
          )}
          <h1 className="text-2xl font-bold text-gray-900">{form.name}</h1>
          {form.description && (
            <p className="text-gray-600 mt-2">{form.description}</p>
          )}
          <p className="text-sm text-blue-600 font-medium mt-2">{form.job_title}</p>
        </div>

        {/* Error banner */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-5">
          {form.fields.map((field) => (
            <div key={field.id}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>

              {field.type === 'text' && (
                <input
                  type="text"
                  value={fieldValues[field.id] || ''}
                  onChange={(e) => setFieldValues((v) => ({ ...v, [field.id]: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  placeholder={field.placeholder}
                />
              )}

              {field.type === 'email' && (
                <input
                  type="email"
                  value={fieldValues[field.id] || ''}
                  onChange={(e) => setFieldValues((v) => ({ ...v, [field.id]: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  placeholder="email@example.com"
                />
              )}

              {field.type === 'phone' && (
                <input
                  type="tel"
                  value={fieldValues[field.id] || ''}
                  onChange={(e) => setFieldValues((v) => ({ ...v, [field.id]: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  placeholder="+1 (555) 000-0000"
                />
              )}

              {field.type === 'textarea' && (
                <textarea
                  value={fieldValues[field.id] || ''}
                  onChange={(e) => setFieldValues((v) => ({ ...v, [field.id]: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-none"
                  rows={4}
                  placeholder={field.placeholder}
                />
              )}

              {field.type === 'select' && (
                <select
                  value={fieldValues[field.id] || ''}
                  onChange={(e) => setFieldValues((v) => ({ ...v, [field.id]: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                >
                  <option value="">Select...</option>
                  {(field.options || []).filter(Boolean).map((opt, i) => (
                    <option key={i} value={opt}>{opt}</option>
                  ))}
                </select>
              )}

              {field.type === 'file' && (
                <FileUploadField
                  field={field}
                  file={fieldFiles[field.id]}
                  onFileChange={(file) =>
                    setFieldFiles((f) => file ? { ...f, [field.id]: file } : (() => { const { [field.id]: _, ...rest } = f; return rest; })())
                  }
                />
              )}

              {field.type === 'checkbox' && (
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={fieldValues[field.id] === 'true'}
                    onChange={(e) =>
                      setFieldValues((v) => ({ ...v, [field.id]: e.target.checked ? 'true' : '' }))
                    }
                    className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-600">{field.label}</span>
                </label>
              )}

              {fieldErrors[field.id] && (
                <p className="text-xs text-red-500 mt-1">{fieldErrors[field.id]}</p>
              )}
            </div>
          ))}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 px-4 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
            style={{
              backgroundColor: btnColor || '#2563eb',
            }}
            onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.opacity = '0.9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Application'
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-xs text-gray-400">
            Powered by{' '}
            <a href="https://ihirefast.com" className="text-blue-500 hover:underline">
              iHireFast
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

function FileUploadField({
  field,
  file,
  onFileChange,
}: {
  field: FormField;
  file?: File;
  onFileChange: (file: File | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const acceptLabel = field.accept === 'image/*' ? 'Images'
    : field.accept === 'video/*' ? 'Videos'
    : field.accept?.includes('.pdf') ? 'Documents (PDF, Word, Text)'
    : 'Any file';

  return (
    <div>
      {file ? (
        <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2 min-w-0">
            <Upload className="w-4 h-4 text-blue-500 flex-shrink-0" />
            <span className="text-sm text-blue-700 truncate">{file.name}</span>
            <span className="text-xs text-blue-400 flex-shrink-0">
              ({(file.size / 1024 / 1024).toFixed(1)}MB)
            </span>
          </div>
          <button
            type="button"
            onClick={() => onFileChange(null)}
            className="text-blue-400 hover:text-blue-600 flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full border-2 border-dashed border-gray-200 rounded-lg p-6 text-center hover:border-blue-300 hover:bg-blue-50/50 transition-colors"
        >
          <Upload className="w-6 h-6 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Click to upload</p>
          <p className="text-xs text-gray-400 mt-1">
            {acceptLabel} &middot; Max {field.maxFileSize || 10}MB
          </p>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={field.accept === '*' ? undefined : field.accept}
        onChange={(e) => onFileChange(e.target.files?.[0] || null)}
        className="hidden"
      />
    </div>
  );
}
